/**
 * Shared presentational components used across the Files page cluster.
 * Pure refactor — no behavior changes; only identical scaffolding is extracted.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TableContainer,
  TableCell,
  TableSortLabel,
  TablePagination,
  Paper,
  LinearProgress,
  Grid,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import LicenseeFilter from '../../components/common/LicenseeFilter';
import { apiClient } from '../../services/api/authApi';
import EmailNotificationDialog, { parseEmailList } from './EmailNotificationDialog';

// ─────────────────────────────────────────────────────────────────────────────
// SortableTableHeaderCell
// ─────────────────────────────────────────────────────────────────────────────

export interface SortableTableHeaderCellProps {
  /** The column key this header controls. */
  columnKey: string;
  /** The currently active sort column. */
  sortBy: string;
  /** Current sort direction. */
  sortDirection: 'asc' | 'desc';
  /** Callback fired when the header is clicked. */
  onSort: (column: string) => void;
  /** Default direction for the sort label when this column is NOT active. */
  defaultDirection?: 'asc' | 'desc';
  children: React.ReactNode;
}

export const SortableTableHeaderCell: React.FC<SortableTableHeaderCellProps> = ({
  columnKey,
  sortBy,
  sortDirection,
  onSort,
  defaultDirection = 'asc',
  children,
}) => (
  <TableCell
    sortDirection={sortBy === columnKey ? sortDirection : false}
    sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: 'action.hover' } }}
    onClick={() => onSort(columnKey)}
  >
    <TableSortLabel
      active={sortBy === columnKey}
      direction={sortBy === columnKey ? sortDirection : defaultDirection}
      hideSortIcon={false}
      sx={{
        pointerEvents: 'none',
        '& .MuiTableSortLabel-icon': {
          opacity: sortBy === columnKey ? 1 : 0.3,
        },
        fontWeight: sortBy === columnKey ? 700 : 600,
      }}
    >
      {children}
    </TableSortLabel>
  </TableCell>
);

// ─────────────────────────────────────────────────────────────────────────────
// FileNameCell
// ─────────────────────────────────────────────────────────────────────────────

export interface FileNameCellProps {
  /** The primary file name to display (bold body2). */
  name: string;
  /** Optional secondary text shown below the name in caption style. */
  subtitle?: React.ReactNode;
}

/**
 * A `<TableCell>` that renders the original file name with an optional subtitle.
 * Extracted to eliminate duplicated JSX in ProcessedFilesPage and UploadedFilesPage.
 */
export const FileNameCell: React.FC<FileNameCellProps> = ({ name, subtitle }) => (
  <TableCell>
    <Box>
      <Typography variant="body2" fontWeight="medium">
        {name}
      </Typography>
      {subtitle !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </TableCell>
);

// ─────────────────────────────────────────────────────────────────────────────
// DeleteIconButton
// ─────────────────────────────────────────────────────────────────────────────

export interface DeleteIconButtonProps {
  onClick: () => void;
}

/**
 * The standard small red delete icon button used in every files-page action column.
 * Extracted to eliminate the repeated identical JSX in all four files pages.
 */
export const DeleteIconButton: React.FC<DeleteIconButtonProps> = ({ onClick }) => (
  <IconButton size="small" onClick={onClick} title="Delete" color="error">
    <DeleteIcon />
  </IconButton>
);

// ─────────────────────────────────────────────────────────────────────────────
// FilesTableContainer
// ─────────────────────────────────────────────────────────────────────────────

export interface FilesTableContainerProps {
  loading: boolean;
  children: React.ReactNode;
}

export const FilesTableContainer: React.FC<FilesTableContainerProps> = ({ loading, children }) => (
  <TableContainer component={Paper} sx={{ position: 'relative' }}>
    <LinearProgress
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1,
        visibility: loading ? 'visible' : 'hidden',
      }}
    />
    {children}
  </TableContainer>
);

// ─────────────────────────────────────────────────────────────────────────────
// FilesTablePagination
// ─────────────────────────────────────────────────────────────────────────────

export interface FilesTablePaginationProps {
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
}

export const FilesTablePagination: React.FC<FilesTablePaginationProps> = ({
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => {
  if (total <= 0) return null;
  return (
    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_, newPage) => onPageChange(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => {
        onRowsPerPageChange(Number.parseInt(e.target.value, 10));
      }}
      rowsPerPageOptions={[5, 10, 25]}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FilesEmptyState
// ─────────────────────────────────────────────────────────────────────────────

export interface FilesEmptyStateProps {
  loading: boolean;
  filesLength: number;
  primaryText: string;
  secondaryText: string;
}

export const FilesEmptyState: React.FC<FilesEmptyStateProps> = ({
  loading,
  filesLength,
  primaryText,
  secondaryText,
}) => {
  if (filesLength !== 0 || loading) return null;
  return (
    <Box textAlign="center" py={4}>
      <Typography variant="h6" color="text.secondary">
        {primaryText}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {secondaryText}
      </Typography>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FilesSearchFilterCard
// ─────────────────────────────────────────────────────────────────────────────

export interface FilesSearchFilterCardProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  isMasterAdmin: boolean;
  filterCompanyId: number | '';
  onFilterCompanyChange: (id: number | '', name: string) => void;
  total: number;
  /** Optional extra grid items rendered after the total count. */
  extraItems?: React.ReactNode;
}

export const FilesSearchFilterCard: React.FC<FilesSearchFilterCardProps> = ({
  searchValue,
  onSearchChange,
  isMasterAdmin,
  filterCompanyId,
  onFilterCompanyChange,
  total,
  extraItems,
}) => (
  <Card sx={{ mb: 3 }}>
    <CardContent sx={{ pb: '16px !important' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="Search files"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ mb: 0 }}
            InputLabelProps={{
              sx: { mt: '6px' },
            }}
          />
        </Grid>
        {isMasterAdmin && (
          <Grid item xs={12} sm={3}>
            <LicenseeFilter
              value={filterCompanyId}
              onChange={(id, name) => onFilterCompanyChange(id, name)}
              sx={{ width: '100%' }}
            />
          </Grid>
        )}
        <Grid item xs sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
            {total} file{total === 1 ? '' : 's'} total
          </Typography>
        </Grid>
        {extraItems}
      </Grid>
    </CardContent>
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────────
// DeleteConfirmDialog
// ─────────────────────────────────────────────────────────────────────────────

export interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** Full confirmation message body. */
  message: React.ReactNode;
}

export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Typography>{message}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="outlined">Cancel</Button>
      <Button onClick={onConfirm} color="error" variant="contained">
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

// ─────────────────────────────────────────────────────────────────────────────
// useFilesEmailSend — shared email-send logic for Calculated & Final pages
// ─────────────────────────────────────────────────────────────────────────────

export interface UseFilesEmailSendOptions {
  fileId: string | undefined;
  customEmails: string;
  selectedUserIds: number[];
  comment: string;
  onSuccess: () => void;
  onError: (message: string) => void;
}

/**
 * Returns a `handleSendEmail` async function that posts to /excel/send-notification.
 * Identical logic previously duplicated in CalculatedFilesPage and FinalFilesPage.
 */
export const buildHandleSendEmail = (opts: UseFilesEmailSendOptions) =>
  async (): Promise<boolean> => {
    const { fileId, customEmails, selectedUserIds, comment, onSuccess, onError } = opts;
    if (!fileId) return false;

    // Selected users and typed addresses are combined; the backend dedupes the union
    // and sends one separate message per address.
    const emails = parseEmailList(customEmails);
    if (selectedUserIds.length === 0 && emails.length === 0) {
      onError('Please select at least one user or enter an email address');
      return false;
    }

    const emailData: any = { fileId };
    if (selectedUserIds.length > 0) emailData.recipientIds = selectedUserIds;
    if (emails.length > 0) emailData.customEmails = emails;

    // Omit when blank so the template drops the message block entirely.
    if (comment.trim()) {
      emailData.comment = comment.trim();
    }

    try {
      const response = await apiClient.post('/excel/send-notification', emailData);
      if (response.data.success) {
        onSuccess();
        return true;
      }
      onError(response.data.error || 'Failed to send email');
      return false;
    } catch (err: any) {
      onError(err.response?.data?.error || 'Failed to send email notification');
      return false;
    }
  };

// ─────────────────────────────────────────────────────────────────────────────
// useEmailDialogState — groups the 6 email-dialog useState declarations that
// were duplicated identically in CalculatedFilesPage and FinalFilesPage.
// ─────────────────────────────────────────────────────────────────────────────

export interface EmailDialogState {
  emailDialogOpen: boolean;
  setEmailDialogOpen: (open: boolean) => void;
  emailLoadingFileId: string | null;
  setEmailLoadingFileId: (id: string | null) => void;
  customEmails: string;
  setCustomEmails: (emails: string) => void;
  emailComment: string;
  setEmailComment: (comment: string) => void;
  sendingEmail: boolean;
  setSendingEmail: (sending: boolean) => void;
  companyUsers: any[];
  setCompanyUsers: (users: any[]) => void;
  selectedUserIds: number[];
  setSelectedUserIds: (ids: number[]) => void;
  /** Resets all email dialog state to initial values. */
  resetEmailDialog: () => void;
}

export const useEmailDialogState = (): EmailDialogState => {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailLoadingFileId, setEmailLoadingFileId] = useState<string | null>(null);
  const [customEmails, setCustomEmails] = useState<string>('');
  const [emailComment, setEmailComment] = useState<string>('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  const resetEmailDialog = () => {
    setEmailDialogOpen(false);
    setCustomEmails('');
    setEmailComment('');
    setSelectedUserIds([]);
    setCompanyUsers([]);
  };

  return {
    emailDialogOpen, setEmailDialogOpen,
    emailLoadingFileId, setEmailLoadingFileId,
    customEmails, setCustomEmails,
    emailComment, setEmailComment,
    sendingEmail, setSendingEmail,
    companyUsers, setCompanyUsers,
    selectedUserIds, setSelectedUserIds,
    resetEmailDialog,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// useHandleSendEmail — wraps buildHandleSendEmail with setSendingEmail.
// Identical pattern was duplicated in CalculatedFilesPage and FinalFilesPage.
// ─────────────────────────────────────────────────────────────────────────────

export const makeHandleSendEmail = (
  emailDialogState: Pick<
    EmailDialogState,
    | 'customEmails'
    | 'emailComment'
    | 'selectedUserIds'
    | 'setSendingEmail'
    | 'resetEmailDialog'
  > & { fileId: string | undefined; setError: (msg: string | null) => void },
): (() => Promise<void>) => {
  const {
    fileId,
    customEmails,
    emailComment,
    selectedUserIds,
    setSendingEmail,
    resetEmailDialog,
    setError,
  } = emailDialogState;

  return async () => {
    setSendingEmail(true);
    const sender = buildHandleSendEmail({
      fileId,
      customEmails,
      comment: emailComment,
      selectedUserIds,
      onSuccess: () => {
        resetEmailDialog();
        setError(null);
      },
      onError: (msg) => setError(msg),
    });
    await sender();
    setSendingEmail(false);
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// FilesTableFooter — combines FilesTablePagination + FilesEmptyState +
// DeleteConfirmDialog. Extracted to eliminate the repeated three-block sequence
// in ProcessedFilesPage and UploadedFilesPage.
// ─────────────────────────────────────────────────────────────────────────────

export interface FilesTableFooterProps {
  /** Props forwarded to FilesTablePagination. */
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (n: number) => void;
  /** Props forwarded to FilesEmptyState. */
  loading: boolean;
  filesLength: number;
  emptyPrimaryText: string;
  emptySecondaryText: string;
  /** Props forwarded to DeleteConfirmDialog. */
  deleteDialogOpen: boolean;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  deleteTitle: string;
  deleteMessage: React.ReactNode;
}

export const FilesTableFooter: React.FC<FilesTableFooterProps> = ({
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading,
  filesLength,
  emptyPrimaryText,
  emptySecondaryText,
  deleteDialogOpen,
  onDeleteClose,
  onDeleteConfirm,
  deleteTitle,
  deleteMessage,
}) => (
  <>
    <FilesTablePagination
      total={total}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
    <FilesEmptyState
      loading={loading}
      filesLength={filesLength}
      primaryText={emptyPrimaryText}
      secondaryText={emptySecondaryText}
    />
    <DeleteConfirmDialog
      open={deleteDialogOpen}
      onClose={onDeleteClose}
      onConfirm={onDeleteConfirm}
      title={deleteTitle}
      message={deleteMessage}
    />
  </>
);

// ─────────────────────────────────────────────────────────────────────────────
// ConnectedEmailNotificationDialog — wraps EmailNotificationDialog with the
// standard onClose reset behaviour shared by CalculatedFilesPage & FinalFilesPage.
// ─────────────────────────────────────────────────────────────────────────────

export interface ConnectedEmailNotificationDialogProps {
  emailDialogState: EmailDialogState;
  fileName: string | null | undefined;
  onSend: () => void;
}

export const ConnectedEmailNotificationDialog: React.FC<ConnectedEmailNotificationDialogProps> = ({
  emailDialogState,
  fileName,
  onSend,
}) => {
  const {
    emailDialogOpen,
    resetEmailDialog,
    companyUsers,
    selectedUserIds,
    setSelectedUserIds,
    customEmails,
    setCustomEmails,
    emailComment,
    setEmailComment,
    sendingEmail,
  } = emailDialogState;

  return (
    <EmailNotificationDialog
      open={emailDialogOpen}
      onClose={resetEmailDialog}
      fileName={fileName}
      companyUsers={companyUsers}
      selectedUserIds={selectedUserIds}
      onSelectedUserIdsChange={setSelectedUserIds}
      customEmails={customEmails}
      onCustomEmailsChange={setCustomEmails}
      comment={emailComment}
      onCommentChange={setEmailComment}
      sending={sendingEmail}
      onSend={onSend}
    />
  );
};
